<template>
  <v-container class="mt-10">
    <a class="anchor" id="exhibitors"></a>
    <div class="col text-center">
      <h2 class="text-center text-uppercase">
        <i class="fas fa-square"></i>
        {{ pageSettings.exhibitorsTitle }}
      </h2>
    </div>
    <p
      class="text-justify"
      v-html="getRichTextString(pageSettings.exhibitorsText, false)"
    ></p>
    <v-data-iterator
      :items="filteredExhibitors"
      :items-per-page.sync="itemsPerPage"
      :page="page"
      :search="search"
      :no-data-text="$t('noEntriesFound')"
      :no-results-text="$t('noEntriesFound')"
      hide-default-footer
    >
      <template #header>
        <v-text-field
          v-model="search"
          outlined
          clearable
          append-icon="mdi-magnify"
          :label="$t('form.fields.search')"
        ></v-text-field>
        <v-chip-group
          multiple
          class="mt-n7 mb-3"
          v-if="tags.length > 0"
          active-class="primary"
          v-model="filterTags"
        >
          <v-chip v-for="tag in tags" :key="tag" filter>
            {{ tag }}
          </v-chip>
        </v-chip-group>
      </template>

      <template #default="props">
        <v-row>
          <v-col
            v-for="item in props.items"
            :key="item.id"
            cols="12"
            md="6"
            lg="4"
            class="d-flex flex-column"
          >
            <v-card class="flex d-flex flex-column">
              <v-card-title class="font-weight-bold">
                <div class="exhibitor-image pa-5">
                  <v-img :src="getLogoUrl(item)" height="70px" contain></v-img>
                </div>
                <v-clamp autoresize :max-lines="1">
                  {{ item.vendorName || item.title }}
                </v-clamp>
              </v-card-title>
              <v-divider></v-divider>
              <v-card-text class="richtext pb-5 flex description-text">
                <p class="mt-n4 mb-2 overline">
                  <v-clamp
                    autoresize
                    :max-lines="1"
                    v-if="item.tags && item.tags.length > 0"
                  >
                    {{
                      getTranslatedTags(item)
                        .sort()
                        .join(", ")
                    }}
                  </v-clamp>
                </p>
                <v-clamp autoresize :max-lines="5">
                  {{
                    getRichTextString(
                      getTranslatedText(
                        item.description_de,
                        item.description_en,
                        item.description_es
                      ),
                      true
                    )
                  }}
                </v-clamp>
              </v-card-text>
              <v-card-actions>
                <v-btn
                  :color="item.color ? item.color : 'primary'"
                  :dark="hasDarkText(item.color)"
                  v-html="$t('form.btn.details')"
                  @click="showDetailView(item)"
                ></v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
      </template>

      <template #footer v-if="numberOfPages > 1">
        <v-row class="mt-2" align="center" justify="center">
          <v-spacer></v-spacer>

          <span
            class="mr-4 grey--text"
            v-html="
              $t('pagination', { page: page, numberOfPages: numberOfPages })
            "
          >
          </span>
          <v-btn
            color="primary"
            class="mr-1"
            :disabled="page === 1"
            @click="formerPage"
          >
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
          <v-btn
            color="primary"
            class="ml-1"
            :disabled="page === numberOfPages"
            @click="nextPage"
          >
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
        </v-row>
      </template>
    </v-data-iterator>

    <v-dialog scrollable v-model="detailView" max-width="1000px" persistent>
      <v-card>
        <v-toolbar
          class="headline"
          :color="currentExhibitor.color ? currentExhibitor.color : 'primary'"
          :dark="hasDarkText(currentExhibitor.color)"
        >
          <v-clamp autoresize :max-lines="1">
            {{ currentExhibitor.vendorName || currentExhibitor.title }}
          </v-clamp>
          <v-spacer></v-spacer>
          <v-btn
            icon
            :dark="hasDarkText(currentExhibitor.color)"
            @click="detailView = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text :style="cssVariables">
          <v-row class="body-1 mt-3">
            <div class="hidden-sm-and-down">
              <div style="width: 30%" class="float-left ma-5 mt-8">
                <v-img :src="getLogoUrl(currentExhibitor)" contain></v-img>
              </div>
              <div
                class="richtext ml-5 mr-5 mt-2 description-text"
                v-html="
                  getRichTextString(
                    getTranslatedText(
                      currentExhibitor.description_de,
                      currentExhibitor.description_en,
                      currentExhibitor.description_es
                    ),
                    false
                  )
                "
              ></div>
            </div>
            <v-col cols="12" class="hidden-md-and-up">
              <v-img :src="getLogoUrl(currentExhibitor)" contain></v-img>
            </v-col>
            <v-col
              class="richtext mr-1 hidden-md-and-up"
              v-html="
                getRichTextString(
                  getTranslatedText(
                    currentExhibitor.description_de,
                    currentExhibitor.description_en,
                    currentExhibitor.description_es
                  ),
                  false
                )
              "
            >
            </v-col>
          </v-row>
          <v-row
            class="exhibitor-row body-1 mt-6"
            v-if="currentExhibitor.meetingUrl"
          >
            <div class="odd-background"></div>
            <v-col cols="12" md="4" class="ml-n1 mr-1">
              <v-btn
                small
                text
                :href="currentExhibitor.meetingUrl"
                target="_blank"
                :color="
                  currentExhibitor.color ? currentExhibitor.color : 'primary'
                "
                class="custom-link"
              >
                <v-icon small left>mdi-webcam</v-icon>
                {{ $t("onlineMeeting") }}
              </v-btn>
            </v-col>
            <v-col
              class="richtext"
              v-html="
                getRichTextString(
                  getTranslatedText(
                    currentExhibitor.meetingUrlInfo_de,
                    currentExhibitor.meetingUrlInfo_en,
                    currentExhibitor.meetingUrlInfo_es
                  )
                )
              "
            >
            </v-col>
          </v-row>
          <v-row
            class="exhibitor-row richtext body-1 mt-6 mb-6 ml-0"
            v-if="currentExhibitor.flyer && currentExhibitor.flyer.length > 0"
          >
            <div
              class="odd-background"
              v-if="!currentExhibitor.meetingUrl"
            ></div>
            <v-col
              class="pl-3 pt-3 pb-0"
              cols="12"
              v-html="
                getRichTextString(
                  getTranslatedText(
                    currentExhibitor.flyerInfo_de,
                    currentExhibitor.flyerInfo_en,
                    currentExhibitor.flyerInfo_es
                  ),
                  false
                )
              "
            >
            </v-col>
            <v-col
              :class="
                `mt-n2 pl-3 pt-${
                  currentExhibitor.flyerInfo_de.length > 0 ? 0 : 3
                } pb-3`
              "
            >
              <span
                v-for="document in currentExhibitor.flyer"
                :key="document.id"
              >
                <v-chip
                  close
                  :color="
                    currentExhibitor.color ? currentExhibitor.color : 'primary'
                  "
                  :text-color="
                    hasDarkText(currentExhibitor.color) ? 'white' : 'black'
                  "
                  close-icon="mdi-download"
                  @click:close="
                    downloadDocument(
                      getTranslatedText(
                        document.file,
                        document.file_en,
                        document.file_es
                      )
                    )
                  "
                  class="mt-1 mb-1 mr-2"
                >
                  <v-avatar left>
                    <v-icon small>mdi-file</v-icon>
                  </v-avatar>
                  {{
                    getTranslatedText(
                      document.title,
                      document.title_en,
                      document.title_es
                    )
                  }}
                </v-chip>
              </span>
            </v-col>
          </v-row>
          <v-row class="exhibitor-row" v-if="hasImageOrVideo(currentExhibitor)">
            <div
              class="odd-background"
              v-if="currentExhibitor.meetingUrl"
            ></div>
            <div class="video-container ml-10 mr-10 mt-6 mb-4">
              <v-carousel
                height="100%"
                hide-delimiters
                :show-arrows="hasSlides(currentExhibitor)"
                show-arrows-on-hover
                v-model="currentSlide"
                @change="centerSelectedSlide"
              >
                <v-carousel-item
                  v-for="(video, index) in currentExhibitor.videos || []"
                  :key="video.id"
                >
                  <iframe
                    v-if="detailView && index === currentSlide"
                    :src="embeddedVideoUrl(video.video)"
                    frameborder="0"
                    allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                    allowfullscreen="allowfullscreen"
                  ></iframe>
                </v-carousel-item>
                <v-carousel-item
                  v-for="picture in currentExhibitor.pictures || []"
                  :key="picture.id"
                >
                  <v-img
                    height="100%"
                    :contain="
                      picture.file.width <= picture.file.height ||
                        !picture.title
                    "
                    :src="
                      picture.file._urls
                        ? picture.file._urls['two-thirds']
                        : picture.file._url
                    "
                  >
                    <v-row align="end" class="lightbox white--text fill-height">
                      <v-toolbar
                        class="headline"
                        :color="
                          currentExhibitor.color
                            ? currentExhibitor.color
                            : 'primary'
                        "
                        v-if="picture.title"
                      >
                        <div
                          :class="
                            `subheading pa-4 ${
                              !hasDarkText(currentExhibitor.color)
                                ? 'black--text'
                                : ''
                            }`
                          "
                          v-html="
                            getTranslatedText(
                              picture.title,
                              picture.title_en,
                              picture.title_es
                            )
                          "
                        ></div>
                      </v-toolbar>
                    </v-row>
                  </v-img>
                </v-carousel-item>
              </v-carousel>
            </div>
          </v-row>
          <v-row
            v-if="hasSlides(currentExhibitor)"
            justify="center"
            class="exhibitor-row"
            ref="sliderParent"
            v-resize="sliderPreviewResize"
          >
            <div
              class="odd-background"
              v-if="currentExhibitor.meetingUrl"
            ></div>
            <div ref="sliderButtons" class="slider-buttons">
              <v-btn-toggle
                v-model="currentSlide"
                mandatory
                @change="centerSelectedSlide"
              >
                <v-btn
                  v-for="(video, index) in currentExhibitor.videos || []"
                  :key="video.id"
                  class="pa-0"
                  :color="
                    currentSlide === index
                      ? currentExhibitor.color
                        ? currentExhibitor.color
                        : 'primary'
                      : undefined
                  "
                >
                  <v-icon
                    :color="
                      currentSlide === index
                        ? 'white'
                        : currentExhibitor.color
                        ? currentExhibitor.color
                        : 'primary'
                    "
                    >mdi-youtube</v-icon
                  >
                </v-btn>
                <v-btn
                  v-for="(picture, index) in currentExhibitor.pictures || []"
                  :key="picture.id"
                  class="pa-0"
                >
                  <v-img
                    height="48px"
                    width="48px"
                    class="pa-2"
                    :src="
                      picture.file._urls
                        ? picture.file._urls['one-sixth']
                        : picture.file._url
                    "
                    contain
                  >
                  </v-img>
                  <v-overlay
                    absolute
                    :color="
                      currentExhibitor.color
                        ? currentExhibitor.color
                        : 'primary'
                    "
                    :value="
                      currentSlide ===
                        index +
                          (currentExhibitor.videos
                            ? currentExhibitor.videos.length
                            : 0)
                    "
                  >
                  </v-overlay>
                </v-btn>
              </v-btn-toggle>
            </div>
          </v-row>
          <v-row
            v-if="hasSlides(currentExhibitor)"
            justify="center"
            class="exhibitor-row"
          >
            <div
              class="odd-background"
              v-if="currentExhibitor.meetingUrl"
            ></div>
            <p class="subtitle-2 font-weight-bold mt-2 mb-2">
              {{ currentSlide + 1 }} /
              {{
                (currentExhibitor.pictures
                  ? currentExhibitor.pictures.length
                  : 0) +
                  (currentExhibitor.videos ? currentExhibitor.videos.length : 0)
              }}
            </p>
          </v-row>
          <v-row
            v-if="currentExhibitor.address"
            class="ma-2 mt-4 subtitle-2 exhibitor-address"
          >
            <span
              class="richtext"
              v-html="getRichTextString(currentExhibitor.address, false)"
            ></span>
          </v-row>
          <v-divider></v-divider>
          <v-row
            v-if="currentExhibitor.email || currentExhibitor.phone"
            class="ml-1"
          >
            <v-btn
              small
              text
              :href="`mailto:${currentExhibitor.email}`"
              :color="
                currentExhibitor.color
                  ? hasDarkText(currentExhibitor.color)
                    ? currentExhibitor.color
                    : 'black'
                  : 'primary'
              "
              class="custom-link"
            >
              <v-icon small left>mdi-email</v-icon>
              {{ currentExhibitor.email }}
            </v-btn>
            <v-btn
              v-if="currentExhibitor.phone"
              small
              text
              :href="`tel:${currentExhibitor.phone}`"
              :color="
                currentExhibitor.color
                  ? hasDarkText(currentExhibitor.color)
                    ? currentExhibitor.color
                    : 'black'
                  : 'primary'
              "
              class="custom-link"
            >
              <v-icon small left>mdi-phone</v-icon>
              {{ currentExhibitor.phone }}
            </v-btn>
          </v-row>
          <v-row class="ma-2">
            <v-btn
              :color="
                currentExhibitor.color ? currentExhibitor.color : 'primary'
              "
              :dark="hasDarkText(currentExhibitor.color)"
              fab
              class="mr-2 custom-link"
              :href="currentExhibitor.url"
              target="_blank"
              small
              v-if="currentExhibitor.url"
            >
              <v-icon>mdi-web</v-icon>
            </v-btn>
            <v-btn
              :color="
                currentExhibitor.color ? currentExhibitor.color : 'primary'
              "
              :dark="hasDarkText(currentExhibitor.color)"
              fab
              class="mr-2 custom-link"
              :href="currentExhibitor.facebookUrl"
              target="_blank"
              small
              v-if="currentExhibitor.facebookUrl"
            >
              <v-icon>mdi-facebook</v-icon>
            </v-btn>
            <v-btn
              :color="
                currentExhibitor.color ? currentExhibitor.color : 'primary'
              "
              :dark="hasDarkText(currentExhibitor.color)"
              fab
              class="mr-2 custom-link"
              :href="currentExhibitor.twitterUrl"
              target="_blank"
              small
              v-if="currentExhibitor.twitterUrl"
            >
              <v-icon>mdi-twitter</v-icon>
            </v-btn>
            <v-btn
              :color="
                currentExhibitor.color ? currentExhibitor.color : 'primary'
              "
              :dark="hasDarkText(currentExhibitor.color)"
              fab
              class="mr-2 custom-link"
              :href="currentExhibitor.youTubeUrl"
              target="_blank"
              small
              v-if="currentExhibitor.youTubeUrl"
            >
              <v-icon>mdi-youtube</v-icon>
            </v-btn>
            <v-btn
              :color="
                currentExhibitor.color ? currentExhibitor.color : 'primary'
              "
              :dark="hasDarkText(currentExhibitor.color)"
              fab
              class="mr-2 custom-link"
              :href="currentExhibitor.linkedInUrl"
              target="_blank"
              small
              v-if="currentExhibitor.linkedInUrl"
            >
              <v-icon>mdi-linkedin</v-icon>
            </v-btn>
            <v-btn
              :color="
                currentExhibitor.color ? currentExhibitor.color : 'primary'
              "
              :dark="hasDarkText(currentExhibitor.color)"
              fab
              class="mr-2 custom-link"
              :href="currentExhibitor.xingUrl"
              target="_blank"
              small
              v-if="currentExhibitor.xingUrl"
            >
              <v-icon>mdi-xing</v-icon>
            </v-btn>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import { i18n } from "../plugins/i18n";
import { sponsorGroupResource, sponsorViewResource } from "../api/resources";
import VClamp from "vue-clamp";
import { boothResource } from "../../../user-area-page/src/api/resources";

export default {
  async mounted() {
    let exhibitors = [];
    if (this.pageSettings.sponsorGroupsExhibitionIds) {
      for (const sponsorGroupId of this.pageSettings
        .sponsorGroupsExhibitionIds) {
        const sponsors = (
          await sponsorGroupResource.get(sponsorGroupId, {
            headers: {
              "x-xsrf-token": this.$cookies.get("oegg-cms.csrf"),
            },
          })
        ).data.sponsors
          .filter((sponsor) => sponsor.approved)
          .map((sponsor) => {
            return { ...sponsor, sponsorGroupId };
          });
        exhibitors = exhibitors.concat(sponsors);
      }
    }
    if (exhibitors.length === 0) {
      const booths = (
        await boothResource.get("", {
          headers: {
            "x-xsrf-token": this.$cookies.get("oegg-cms.csrf"),
          },
        })
      ).data;
      booths.results.forEach((booth) => {
        if (booth.approved) {
          exhibitors.push(booth);
        } else if (booth.lastApprovedVersion) {
          exhibitors.push(JSON.parse(booth.lastApprovedVersion));
        }
        if (booth.additionalExhibitors && booth.additionalExhibitors.length) {
          booth.additionalExhibitors.forEach((additionalExhibitor) => {
            if (additionalExhibitor.approved) {
              exhibitors.push(additionalExhibitor);
            } else if (additionalExhibitor.lastApprovedVersion) {
              exhibitors.push(
                JSON.parse(additionalExhibitor.lastApprovedVersion)
              );
            }
          });
        }
      });
    }
    const localeTags = [];
    exhibitors.forEach((exhibitor) => {
      if (!exhibitor.tags) {
        return;
      }
      exhibitor.tags.forEach((tag) => {
        tag.title = tag.title.trim().toUpperCase();
        const existingTag = localeTags.find(
          (element) => element.de === tag.title
        );
        if (!existingTag) {
          localeTags.push({
            de: tag.title,
            en: tag.title_en ? tag.title_en.trim().toUpperCase() : tag.title,
            es: tag.title_es
              ? tag.title_es.trim().toUpperCase()
              : tag.title_en
              ? tag.title_en.trim().toUpperCase()
              : tag.title,
          });
        } else if (tag.title_en) {
          existingTag.en = tag.title_en.trim().toUpperCase();
        }
      });
    });
    this.tags = localeTags.map((tag) => tag[this.exhibitionLocale]).sort();
    // shuffle
    this.exhibitors = exhibitors.sort(() => Math.random() - 0.5);
  },
  components: {
    VClamp,
  },
  data: () => ({
    page: 1,
    itemsPerPage: 6,
    currentSlide: 0,
    search: "",
    currentExhibitor: {},
    detailView: false,
    exhibitors: [],
    tags: [],
    filterTags: [],
  }),
  watch: {
    pageSettings() {
      if (!this.pageSettings) {
        return;
      }
    },
  },
  computed: {
    ...mapGetters(["pageSettings", "embeddedVideoUrl"]),
    exhibitionLocale() {
      return i18n.locale;
    },
    numberOfPages() {
      return Math.ceil(this.filteredExhibitors.length / this.itemsPerPage);
    },
    filteredExhibitors() {
      if (this.filterTags.length === 0) {
        return this.exhibitors;
      }
      const tagNames = this.filterTags.map((index) => this.tags[index]);
      return this.exhibitors.filter((exhibitor) =>
        this.getTranslatedTags(exhibitor).find(
          (tag) => tagNames.indexOf(tag) >= 0
        )
      );
    },
    cssVariables() {
      return {
        "--scrollbar-color": this.currentExhibitor.color
          ? this.currentExhibitor.color
          : "#a21c26",
      };
    },
  },
  methods: {
    getLogoUrl(exhibitor) {
      if (exhibitor.image && exhibitor.image._urls) {
        return exhibitor.image._urls["one-half"];
      }
      if (exhibitor.logo && exhibitor.logo._urls) {
        return exhibitor.logo._urls["one-half"];
      }
      if (exhibitor.logo && exhibitor.logo._url) {
        return exhibitor.logo._url;
      }
      return "/img/logo-placeholder.jpg";
    },
    getTranslatedText(germanText, englishText, spanishText) {
      if (this.exhibitionLocale === "en") {
        return englishText || germanText || spanishText;
      }
      if (this.exhibitionLocale === "es") {
        return spanishText || englishText || germanText;
      }
      return germanText || englishText || spanishText;
    },
    hasDarkText(color) {
      if (!color) {
        return true;
      }
      let r = 0;
      let g = 0;
      let b = 0;
      if (color.match(/^rgb/)) {
        color = color.match(
          /^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+(?:\.\d+)?))?\)$/
        );
        r = color[1];
        g = color[2];
        b = color[3];
      } else {
        color = +(
          "0x" + color.slice(1).replace(color.length < 5 && /./g, "$&$&")
        );
        r = color >> 16;
        g = (color >> 8) & 255;
        b = color & 255;
      }
      const hsp = Math.sqrt(
        0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b)
      );
      return hsp < 200;
    },
    getRichTextString(richText, stripHtmlTags) {
      let htmlText = "";
      if (!richText || !richText.items) {
        return htmlText;
      }
      htmlText = richText.items
        .map((element) => element.content.replaceAll("<p><br />", "<p>"))
        .join();
      if (stripHtmlTags) {
        const tmp = document.createElement("DIV");
        tmp.innerHTML = htmlText;
        return tmp.textContent || tmp.innerText || "";
      }
      return htmlText;
    },
    getTranslatedTags(exhibitor) {
      if (!exhibitor.tags) {
        return [];
      }
      return exhibitor.tags.map((tag) =>
        (this.exhibitionLocale === "en" && tag.title_en
          ? tag.title_en
          : tag.title
        )
          .trim()
          .toUpperCase()
      );
    },
    hasImageOrVideo(exhibitor) {
      if (exhibitor.videos && exhibitor.videos.length > 0) {
        return true;
      }
      if (exhibitor.pictures && exhibitor.pictures.length > 0) {
        return true;
      }
      return false;
    },
    hasSlides(exhibitor) {
      let slideCount = 0;
      if (exhibitor.videos) {
        slideCount += exhibitor.videos.length;
      }
      if (exhibitor.pictures) {
        slideCount += exhibitor.pictures.length;
      }
      return slideCount > 1;
    },
    nextPage() {
      if (this.page + 1 <= this.numberOfPages) {
        this.page += 1;
      }
    },
    formerPage() {
      if (this.page - 1 >= 1) {
        this.page -= 1;
      }
    },
    showDetailView(exhibitor) {
      this.currentExhibitor = exhibitor;
      this.currentSlide = 0;
      this.detailView = true;
      sponsorViewResource.post(
        "",
        { sponsorGroupId: exhibitor.sponsorGroupId, sponsorId: exhibitor.id },
        {
          headers: {
            "x-xsrf-token": this.$cookies.get("oegg-cms.csrf"),
          },
        }
      );
    },
    downloadDocument(document) {
      let fileUrl = document._url;
      if (!fileUrl && document._urls) {
        fileUrl = document._urls.original;
      }
      window.open(fileUrl, "_blank");
    },
    centerSelectedSlide() {
      const overalWidth = this.$refs.sliderParent.clientWidth;
      const sliderWidth = this.$refs.sliderButtons.clientWidth;
      let left = 0;

      // check that selected slide is still visible on mobile view by centering
      if (sliderWidth >= overalWidth) {
        let slideCount = 0;
        if (this.currentExhibitor.videos) {
          slideCount += this.currentExhibitor.videos.length;
        }
        if (this.currentExhibitor.pictures) {
          slideCount += this.currentExhibitor.pictures.length;
        }
        const slideWidth = sliderWidth / slideCount;
        const selectionX = slideWidth * this.currentSlide;
        const minimumShift = (sliderWidth - overalWidth) / 2;
        if (selectionX < minimumShift * 2) {
          left = minimumShift;
        } else if (selectionX > overalWidth - minimumShift) {
          left = -minimumShift;
        }
      }
      this.$refs.sliderButtons.style.left = `${left}px`;
    },
    sliderPreviewResize() {
      this.$nextTick(() => this.centerSelectedSlide());
    },
  },
};
</script>

<style scoped>
.exhibitor-image {
  width: 100%;
}
.video-container {
  position: relative;
  padding-bottom: 56.25%; /* 16:9 */
  width: 100%;
  height: 0;
}
.video-container iframe,
.video-container > div {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
.video-container header {
  opacity: 0.8;
}
.richtext {
  margin-bottom: -16px;
}
.v-dialog__content {
  z-index: 9999;
}
.v-card__text {
  overflow-x: hidden;
}
.v-card__text::-webkit-scrollbar {
  width: 12px;
}
.v-card__text::-webkit-scrollbar-thumb {
  background: var(--scrollbar-color);
}
.exhibitor-row {
  position: relative;
}
.odd-background {
  position: absolute;
  background-color: var(--scrollbar-color);
  opacity: 0.08;
  width: 200%;
  margin-left: -24px;
  height: 100%;
}
.slider-buttons {
  position: relative;
}
.exhibitor-address >>> p:not(:last-child) {
  margin-bottom: 0;
}
</style>
